import { DropdownOption } from 'drive-ui-packages';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AUTHORITIES } from 'common/constants';
import { useClient } from 'components/ClientProvider';
import { CheckSequenceOverflowTrayDropdown } from 'pages/VendorsTablePage/components/PrintChecks/CheckSequence/CheckSequenceOverflowTrayDropdown';
import { CheckSequencePrinterDropdown } from 'pages/VendorsTablePage/components/PrintChecks/CheckSequence/CheckSequencePrinterDropdown';
import { useAppDispatch } from 'store/hooks';
import { setArchiveOnly, setDmsPrinterNo, setPrinterId } from '../slice';
import { CheckSequenceCheckTrayDropdown } from './CheckSequenceCheckTrayDropdown';
import { ARCHIVE_ONLY } from './constants';
import { EditButton } from './Editoption';
import {
  checkAuthorities,
  getCheckTrayAndOverflowOptions,
  getDefaultPrinter,
  getDefaultPrinterInfo,
  getDefaultStore,
  getPrinterErrors,
  getSelectedPrinter,
  mapPermissionsToAuthorities
} from './helpers';
import { messages } from './messages';
import { ResetButton } from './ResetButton';
import { setAllPrinterErrors, setSelectedPrinterResponse } from './slice';
import { StyledCheckSequence, StyledColumn } from './styled';
import {
  CheckSequenceDropdownsType,
  PrinterAndCheckNoResponseType
} from './types';

export const CheckSequenceDropdowns = ({
  isEdit,
  setIsEdit,
  printerAndCheckData,
  isError
}: CheckSequenceDropdownsType): React.JSX.Element => {
  const intl = useIntl();
  const userInfo = useClient();
  const dispatch = useAppDispatch();
  const userPermissions = userInfo?.customClientValues?.permissions;
  const [printerOptions, setPrinterOptions] = useState<DropdownOption[]>([]);
  const [trayOptions, setTrayOptions] = useState<DropdownOption[]>([]);
  const [selectedTray, setSelectedTray] = useState<DropdownOption | null>(null);
  const [overflowTrayOptions, setOverflowTrayOptions] = useState<
    DropdownOption[]
  >([]);
  const [selectedOverflowTray, setSelectedOverflowTray] =
    useState<DropdownOption | null>(null);
  const [selectedPrinterName, setSelectedPrinterName] =
    useState<DropdownOption | null>(null);

  const updatedPermission = mapPermissionsToAuthorities(userPermissions);

  const userCanOverridePrinterSettings = checkAuthorities(
    updatedPermission,
    AUTHORITIES.OVERRIDE_PRINTER_SETTINGS_AP_CHECK
  );

  const findAndSetPrinterError = useCallback(
    (data: PrinterAndCheckNoResponseType[]) => {
      const defaultPrinter = getDefaultPrinterInfo(data);
      const { printerError, checkTrayError, overflowTrayError } =
        getPrinterErrors(defaultPrinter);
      dispatch(
        setAllPrinterErrors({
          printerError,
          checkTrayError,
          overflowTrayError
        })
      );
    },
    [dispatch]
  );
  const defaultPrinterInfo = useMemo(
    () => getDefaultPrinterInfo(printerAndCheckData),
    [printerAndCheckData]
  );

  const setPrinterData = useCallback(
    async (data: PrinterAndCheckNoResponseType[]) => {
      if (data) {
        const options = getDefaultPrinter(data);
        options.push({
          label: intl.formatMessage(messages.archiveOnly),
          value: ARCHIVE_ONLY
        });
        setPrinterOptions(options);
        const defaultOption = getDefaultStore(data);
        setSelectedPrinterName(defaultOption);
        if (defaultOption) {
          const selectedPrinter = getSelectedPrinter(data, defaultOption);
          const trayOption = getCheckTrayAndOverflowOptions(
            defaultOption.label,
            data,
            true
          );
          setTrayOptions(trayOption);
          setSelectedTray(trayOption[0]);
          const overflowTrayOption = getCheckTrayAndOverflowOptions(
            defaultOption.label,
            data,
            false
          );
          setOverflowTrayOptions(overflowTrayOption);
          setSelectedOverflowTray(overflowTrayOption[0]);
          dispatch(setSelectedPrinterResponse(selectedPrinter));
          dispatch(setDmsPrinterNo(selectedPrinter?.dmsPrinterNo));
          dispatch(setPrinterId(selectedPrinter?.printerId));
          dispatch(setArchiveOnly(false));
        }
      }
      findAndSetPrinterError(data);
    },
    [findAndSetPrinterError, intl, dispatch]
  );

  const handlePrinterChange = (data: DropdownOption) => {
    setSelectedPrinterName(data);
    if (data) {
      if (data.value === ARCHIVE_ONLY) {
        setTrayOptions([]);
        setSelectedTray({ label: ' ', value: ' ' });
        setOverflowTrayOptions([]);
        setSelectedOverflowTray({ label: ' ', value: ' ' });
        dispatch(setArchiveOnly(true));
        dispatch(
          setAllPrinterErrors({
            printerError: false,
            checkTrayError: false,
            overflowTrayError: false
          })
        );

        dispatch(setDmsPrinterNo(defaultPrinterInfo?.dmsPrinterNo));
        dispatch(setPrinterId(defaultPrinterInfo?.printerId));
      } else if (printerAndCheckData) {
        const trayOption = getCheckTrayAndOverflowOptions(
          data.label,
          printerAndCheckData,
          true
        );
        setTrayOptions(trayOption);
        setSelectedTray(trayOption[0]);
        const selectedPrinter = printerAndCheckData.find(
          printer => printer.printerDetails?.printerName === data?.value
        );
        const overflowTrayOption = getCheckTrayAndOverflowOptions(
          data.label,
          printerAndCheckData,
          false
        );
        setOverflowTrayOptions(overflowTrayOption);
        setSelectedOverflowTray(overflowTrayOption[0]);
        dispatch(setSelectedPrinterResponse(selectedPrinter));
        dispatch(setDmsPrinterNo(selectedPrinter?.dmsPrinterNo));
        dispatch(setPrinterId(selectedPrinter?.printerId));
        dispatch(setArchiveOnly(false));
      }
    }
  };

  const handleEditToggle = (data: boolean) => {
    setIsEdit(data);
    if (data && printerAndCheckData) {
      setPrinterData(printerAndCheckData);
    } else {
      setSelectedPrinterName(null);
      setSelectedTray(null);
      setSelectedOverflowTray(null);
    }
  };

  useEffect(() => {
    if (printerAndCheckData && printerAndCheckData?.length > 0) {
      setPrinterData(printerAndCheckData);
      findAndSetPrinterError(printerAndCheckData);
    }
  }, [findAndSetPrinterError, printerAndCheckData, setPrinterData]);

  useEffect(() => {
    if (isError) {
      dispatch(setArchiveOnly(false));
      setPrinterOptions([
        {
          label: intl.formatMessage(messages.archiveOnly),
          value: ARCHIVE_ONLY
        }
      ]);
      setSelectedPrinterName({ label: '', value: '' });
      setTrayOptions([]);
      setSelectedTray({ label: '', value: '' });
      setOverflowTrayOptions([]);
      setSelectedOverflowTray({ label: '', value: '' });
    }
  }, [intl, isError, dispatch]);

  return (
    <>
      {isEdit && (
        <StyledCheckSequence>
          <StyledColumn>
            <CheckSequencePrinterDropdown
              data-testid="printer-dropdown"
              printers={printerOptions}
              setSelectedPrinter={handlePrinterChange}
              isLoading={false}
              defaultOption={selectedPrinterName}
            />
            <CheckSequenceCheckTrayDropdown
              data-testid="check-tray-dropdown"
              checkTrayValue={trayOptions}
              setSelectedCheckTray={setSelectedTray}
              isLoading={false}
              defaultOption={selectedTray}
            />
            <CheckSequenceOverflowTrayDropdown
              data-testid="overflow-tray-dropdown"
              overflowTrayValue={overflowTrayOptions}
              setSelectedOverflowTray={setSelectedOverflowTray}
              isLoading={false}
              defaultOption={selectedOverflowTray}
            />
            <ResetButton
              setReset={() => {
                setPrinterData(printerAndCheckData);
                dispatch(setArchiveOnly(false));
              }}
              isEdit={isEdit}
              setIsEdit={handleEditToggle}
            />
          </StyledColumn>
        </StyledCheckSequence>
      )}
      {!isEdit && userCanOverridePrinterSettings && (
        <EditButton
          data-testid="edit-btn"
          isEdit={isEdit}
          setIsEdit={handleEditToggle}
        />
      )}
    </>
  );
};
